import * as jsep from './lib/jsep';
import { clone, isNumber } from '@shared/helpers/arrays';

// These operators won't be accepted when validating
const removeUnusedOperators = () => {
  jsep.removeUnaryOp('~');
  jsep.removeUnaryOp('!');
  jsep.removeUnaryOp('+');

  jsep.removeBinaryOp('&');
  jsep.removeBinaryOp('&&');
  jsep.removeBinaryOp('|');
  jsep.removeBinaryOp('||');
  jsep.removeBinaryOp('==');
  jsep.removeBinaryOp('===');
  jsep.removeBinaryOp('!==');
  jsep.removeBinaryOp('<<');
  jsep.removeBinaryOp('>>');
  jsep.removeBinaryOp('>>>');
};

removeUnusedOperators();

export const beautifyEquation = (equation: string) => {
  if (equation === null) {
    equation = '';
  }

  const binaryOperators = ['+', '-', '/', '*', '<', '>', '^'];
  let beautified = clone(equation);

  // Add spaces around operators
  binaryOperators.forEach(op => {
    const rop = `\\${op}`;
    const noSpaceBefore = new RegExp(`(\\S)${rop}`, 'g');
    const noSpaceAfter = new RegExp(`${rop}(\\S)`, 'g');
    beautified = beautified.replace(noSpaceBefore, `$1 ${op}`).replace(noSpaceAfter, `${op} $1`);
  });

  beautified =
    beautified
      .trim()
      .replace(/(\S)\s+\,/g, '$1,') // Remove space before comma
      .replace(/\,(\S)/g, ', $1') // Add space after comma
      .replace(/\(\s+(\S)/g, '($1') // Remove space after open parenthesis
      .replace(/(\S)\s+\)/g, '$1)') // Remove space before close parenthesis
      // .replace(/(\w)\s+(\w)/g, '$1 * $2') // Add missing * operators (e.g. '4 LT_ASSETS' => '4 * LT_ASSETS')
      // .replace(/(\d)\s*(\()/g, '$1 * $2') // Add missing * operators (e.g. '4 (LT_ASSETS)' => '4 * (LT_ASSETS)')
      // .replace(/(\))\s*(\d)/g, '$1 * $2') // Add missing * operators (e.g. '(LT_ASSETS) 4' => '(LT_ASSETS) * 4')
      .replace(/\s+/g, ' ') + ' '; // Removing double spaces
  // console.log({ equation, beautified });
  return beautified;
};

export const minifyEquation = (equation: string) => {
  let minified = clone(equation);
  minified = minified.replace(/\s/g, '');
  return minified;
};

// /**
//  *
//  * @description Returns either true, if a string has correctly paired brackets, or the character index where a problem occurs
//  */
// export const checkBrackets = (expression: string) => {
//   const left = '(',
//     right = ')';
//   const n = expression.length;
//   let i = 0;
//   let tally = 0;

//   while (i < n) {
//     if (expression[i] === left) {
//       ++tally;
//     } else if (expression[i] === right) {
//       --tally;
//     }
//     if (tally < 0) {
//       break;
//     }
//     ++i;
//   }

//   return (i === n && tally === 0) || i;
// };

export interface EquationEvaluation {
  valid: boolean;
  message: string;
  expression: any;
  index: number;
}

const getIdentifiers = (jsepResult, identifiers = [], functions = []) => {
  if (jsepResult.type === 'BinaryExpression') {
    getIdentifiers(jsepResult.left, identifiers, functions);
    getIdentifiers(jsepResult.right, identifiers, functions);
  } else if (jsepResult.type === 'CallExpression') {
    if (jsepResult.callee.type === 'Identifier') {
      if (!functions.includes(jsepResult.callee.name)) {
        functions.push(jsepResult.callee.name);
      }
    }
    jsepResult.arguments.forEach(expression => {
      getIdentifiers(expression, identifiers, functions);
    });
  } else if (jsepResult.type === 'Identifier') {
    if (!identifiers.includes(jsepResult.name) && !isNumber(jsepResult.name)) {
      identifiers.push(jsepResult.name);
    }
  }
};

export const evaluateExpression = (
  expression: string,
  allFunctions: string[],
  allIdentifiers: string[]
): EquationEvaluation => {
  // const minified = minifyEquation(expression);

  const minified = expression.trim();

  // BUG FIX 93829: Handle empty expressions gracefully - only if truly empty
  if (!minified || minified === '') {
    return {
      valid: true,
      message: 'No syntax errors detected',
      expression: minified,
      index: null
    };
  }

  const identifiers = [],
    functions = [];
  let jsepResult = null;

  let result: EquationEvaluation = {
    valid: true,
    message: null,
    expression: minified,
    index: null
  };
  const startsWithEquals = minified.match(/^(=)/);

  if (startsWithEquals === null) {
    result = {
      valid: false,
      message: 'No beginning equal sign',
      expression: minified,
      index: 1
    };
    return result;
  } else {
    try {
      // removeUnusedOperators();
      jsepResult = jsep(minified.substring(1));

      if (allFunctions.length > 0 || allIdentifiers.length > 0) {
        if (jsepResult !== null) {
          getIdentifiers(jsepResult, identifiers, functions);
        }
        const unknownMessages = [];
        const unknownFunctions = [];
        const unknownIdentifiers = [];

        // Find strange functions or identifiers
        functions.forEach(funct => {
          if (!allFunctions.includes(funct.toLowerCase())) {
            unknownFunctions.push(funct);
          }
        });
        if (unknownFunctions.length > 0) {
          unknownMessages.push(
            `Unknown ${unknownFunctions.length > 1 ? 'functions' : 'function'}: ${unknownFunctions.join(', ')}`
          );
        }

        identifiers.forEach(identifier => {
          if (!allIdentifiers.includes(identifier.toLowerCase())) {
            unknownIdentifiers.push(identifier);
          }
        });
        if (unknownIdentifiers.length > 0) {
          unknownMessages.push(
            `Unknown ${unknownIdentifiers.length > 1 ? 'identifiers' : 'identifier'}: ${unknownIdentifiers.join(', ')}`
          );
        }

        if (unknownMessages.length > 0) {
          result = {
            valid: false,
            message: `${unknownMessages.join(', ')}`,
            expression: minified,
            index: 1
          };
        }
      }

      if (result.valid) {
        result.message = 'No syntax errors detected';
      }
    } catch (error) {
      result = {
        valid: false,
        message: `Equation is incomplete: ${error.message}`,
        expression: minified,
        index: error.index
      };
    } finally {
      return result;
    }
  }
};
